/**
 * NPC daily life — schedules and poses as PURE FUNCTIONS of world time.
 *
 * The Majora's Mask model (see docs/design/npc-life-research.md): an NPC's
 * position at any instant is computed analytically from (persona seed,
 * world time, town layout) — never simulated, never stored, never
 * networked. Every client renders the identical town life; the server
 * evaluates the same function only on demand (validating a talk). Arrive
 * after any absence and everyone is exactly where the clock says.
 *
 * A day is the planet's real rotation. Each role has a slot template
 * (sleep → work → eat → social …); slot starts jitter per-NPC-per-day
 * (never enough to reorder), and the day's variation comes from VARIANT
 * picks, not timeline noise. Between anchors the NPC walks a straight
 * two-leg path at NPC_WALK m/s; at an anchor it drifts on 90-second
 * micro-steps inside the activity radius.
 */
import { townLayout, townRadiusAt, type BuildingDef, type TownDef, type TownLayout } from "./towns.js";
import * as pmath from "./pmath.js";
import { planetPoseAt, type SystemDef } from "./universe.js";
import { navLosFree, navSnapFree, pathAt, walkPath } from "./npcNav.js";
import type { NpcDef } from "./npcs.js";

export type NpcActivity =
  | "sleep" // home — rendered lying in bed when indoors
  | "work" // role anchor: stall, gate, field, workshop
  | "eat" // home or the hall
  | "social" // plaza / the hall ("tavern")
  | "stroll" // wandering the lanes
  | "errand" // 2026-07-31: a shop visit — customers browsing the market
  | "worship"; // 2026-07-31: a shrine/temple visit

export interface NpcPose {
  /** Town-local meters (the layout frame). */
  u: number;
  v: number;
  activity: NpcActivity;
  /** True while walking between anchors (drives the gait). */
  moving: boolean;
  /** Walk direction (town-local radians) while moving. */
  heading: number;
  /** Ground speed m/s (0 when idling). */
  speed: number;
  /** Fraction of the current day 0..1 (0 = local midnight) — mood/dialog. */
  dayFrac: number;
  /** The day index — salts per-day variation and dialog. */
  dayIndex: number;
  /** Working low — a farmer tending the row, an artisan at the bench. */
  crouch?: boolean;
  /** P4 indoor life (2026-07-31): stationed INSIDE the anchor parcel's
   *  walk-in building (u/v sit within the footprint; the client clamps the
   *  pose to the interior floor and applies indoor view/talk rules). */
  indoors?: boolean;
  /** The anchor parcel index (region graph) behind an indoor pose. */
  parcel?: number;
}

export const NPC_WALK = 1.9; // m/s — an unhurried walking pace (base)

/** Personal gait: everyone walks their own pace (0.86–1.12 × base) —
 *  identical speeds read as a marching column when a slot turns over. */
export function npcWalkSpeedForId(id: string): number {
  return NPC_WALK * (0.86 + 0.26 * h01(hash32(seedInt(id), 55)));
}

export function npcWalkSpeed(npc: NpcDef): number {
  return npcWalkSpeedForId(npc.id);
}

/** Deterministic 32-bit hash of small ints (schedule salts). */
export function hash32(a: number, b: number, c = 0, d = 0): number {
  let h = (a | 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (b | 0), 2654435761);
  h = Math.imul(h ^ (c | 0), 2246822519);
  h = Math.imul(h ^ (d | 0), 3266489917);
  h ^= h >>> 15;
  return h >>> 0;
}
const h01 = (h: number): number => h / 4294967296;

/** Seed-string → small int for hashing (persona ids are strings). */
export function seedInt(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Local solar time — dayFrac 0 = the settlement's OWN midnight
// ---------------------------------------------------------------------------

/**
 * The town's local solar day fraction at wall-clock t (seconds). Uses the
 * live system poses, so it agrees with the rendered day/night terminator:
 * NPCs sleep when THEIR sky is dark, which is most of believability.
 */
export function directionDayFrac(
  def: SystemDef, planetIdx: number, dir: ArrayLike<number>, tSec: number,
): number {
  const planet = def.planets[planetIdx];
  const pose = planetPoseAt(def, planetIdx, tSec);
  const axis = planet.spin.axis;
  // Direction to the sun (system origin) in the planet's LOCAL frame.
  const cl = pmath.hypot(pose.center[0], pose.center[1], pose.center[2]) || 1;
  let sx = -pose.center[0] / cl;
  let sy = -pose.center[1] / cl;
  let sz = -pose.center[2] / cl;
  // Rotate world→local: inverse spin about axis (Rodrigues, angle = -spin).
  const ca = pmath.cos(-pose.spinAngle);
  const sa = pmath.sin(-pose.spinAngle);
  const dot = axis[0] * sx + axis[1] * sy + axis[2] * sz;
  const cx = axis[1] * sz - axis[2] * sy;
  const cy = axis[2] * sx - axis[0] * sz;
  const cz = axis[0] * sy - axis[1] * sx;
  const lx = sx * ca + cx * sa + axis[0] * dot * (1 - ca);
  const ly = sy * ca + cy * sa + axis[1] * dot * (1 - ca);
  const lz = sz * ca + cz * sa + axis[2] * dot * (1 - ca);
  // Azimuths about the spin axis (projected off-axis components).
  const az = (x: number, y: number, z: number): number => {
    const d2 = axis[0] * x + axis[1] * y + axis[2] * z;
    const px = x - axis[0] * d2;
    const py = y - axis[1] * d2;
    const pz = z - axis[2] * d2;
    // Reference frame: any stable basis ⊥ axis.
    const ax = Math.abs(axis[0]) < 0.9 ? 1 : 0;
    const ay = 1 - ax;
    let b1x = ay * axis[2];
    let b1y = -ax * axis[2];
    let b1z = ax * axis[1] - ay * axis[0];
    const l = pmath.hypot(b1x, b1y, b1z) || 1;
    b1x /= l;
    b1y /= l;
    b1z /= l;
    const b2x = axis[1] * b1z - axis[2] * b1y;
    const b2y = axis[2] * b1x - axis[0] * b1z;
    const b2z = axis[0] * b1y - axis[1] * b1x;
    return pmath.atan2(px * b2x + py * b2y + pz * b2z, px * b1x + py * b1y + pz * b1z);
  };
  const sunAz = az(lx, ly, lz);
  const townAz = az(dir[0], dir[1], dir[2]);
  // Town at the sun's azimuth = local NOON (dayFrac 0.5).
  let frac = (townAz - sunAz) / (Math.PI * 2) + 0.5;
  frac -= Math.floor(frac);
  return frac;
}

export function townDayFrac(def: SystemDef, planetIdx: number, town: TownDef, tSec: number): number {
  return directionDayFrac(def, planetIdx, town.dir, tSec);
}

/** Absolute day count for a town (salts per-day schedule variation). */
export function townDayIndex(def: SystemDef, planetIdx: number, town: TownDef, tSec: number): number {
  const period = def.planets[planetIdx].spin.periodS;
  // Continuous day float: whole rotations elapsed, phase-aligned via frac.
  const frac = townDayFrac(def, planetIdx, town, tSec);
  return Math.floor(tSec / period - frac + 0.5);
}

// ---------------------------------------------------------------------------
// Role schedule templates (dayFrac; 0 = midnight, 0.5 = noon)
// ---------------------------------------------------------------------------

/** 2026-07-31 richer schedules: `market` (shops — errand targets), `shrine`
 *  (faith parcels — worship), `tavern` (inns/bathhouses — evening social). */
export type NpcScheduleAnchor = "home" | "work" | "plaza" | "hall" | "stroll"
  | "market" | "shrine" | "tavern";

export interface NpcScheduleSlot {
  t: number;
  activity: NpcActivity;
  anchor: NpcScheduleAnchor;
}

// 2026-07-31 richer rhythms: errands put CUSTOMERS in the shops, worship
// fills the sanctums, tavern evenings fill the inns. Slot gaps stay ≥ 0.06
// (the ±0.02 jitter's order-safety bound).
export const NPC_SCHEDULES: Readonly<Record<NpcDef["role"], readonly NpcScheduleSlot[]>> = {
  keeper: [
    { t: 0.29, activity: "work", anchor: "work" },
    { t: 0.52, activity: "eat", anchor: "hall" },
    { t: 0.58, activity: "work", anchor: "work" },
    { t: 0.72, activity: "worship", anchor: "shrine" },
    { t: 0.8, activity: "social", anchor: "plaza" },
    { t: 0.9, activity: "sleep", anchor: "home" },
  ],
  trader: [
    { t: 0.3, activity: "work", anchor: "work" },
    { t: 0.53, activity: "eat", anchor: "home" },
    { t: 0.59, activity: "work", anchor: "work" },
    { t: 0.8, activity: "social", anchor: "tavern" },
    { t: 0.92, activity: "sleep", anchor: "home" },
  ],
  warden: [
    { t: 0.27, activity: "work", anchor: "work" },
    { t: 0.5, activity: "eat", anchor: "hall" },
    { t: 0.56, activity: "work", anchor: "work" },
    { t: 0.83, activity: "social", anchor: "tavern" },
    { t: 0.93, activity: "sleep", anchor: "home" },
  ],
  farmer: [
    { t: 0.24, activity: "work", anchor: "work" },
    { t: 0.5, activity: "eat", anchor: "home" },
    { t: 0.56, activity: "work", anchor: "work" },
    { t: 0.7, activity: "errand", anchor: "market" },
    { t: 0.78, activity: "social", anchor: "hall" },
    { t: 0.88, activity: "sleep", anchor: "home" },
  ],
  artisan: [
    { t: 0.28, activity: "work", anchor: "work" },
    { t: 0.55, activity: "eat", anchor: "home" },
    { t: 0.61, activity: "work", anchor: "work" },
    { t: 0.7, activity: "errand", anchor: "market" },
    { t: 0.78, activity: "social", anchor: "tavern" },
    { t: 0.91, activity: "sleep", anchor: "home" },
  ],
  wanderer: [
    { t: 0.32, activity: "stroll", anchor: "stroll" },
    { t: 0.48, activity: "errand", anchor: "market" },
    { t: 0.55, activity: "eat", anchor: "tavern" },
    { t: 0.62, activity: "stroll", anchor: "stroll" },
    { t: 0.82, activity: "social", anchor: "plaza" },
    { t: 0.95, activity: "sleep", anchor: "home" },
  ],
  elder: [
    { t: 0.27, activity: "worship", anchor: "shrine" },
    { t: 0.38, activity: "social", anchor: "plaza" },
    { t: 0.55, activity: "eat", anchor: "home" },
    { t: 0.63, activity: "social", anchor: "plaza" },
    { t: 0.8, activity: "worship", anchor: "shrine" },
    { t: 0.9, activity: "sleep", anchor: "home" },
  ],
};

// ---------------------------------------------------------------------------
// Anchor resolution — schedule stops are REAL places in the layout
// ---------------------------------------------------------------------------

/** The NPC's home: the building they were spawned FROM (population scales
 *  with the built stock — see npcCapacity). Canon/legacy personas without
 *  a home index fall back to a stable hash-picked dwelling. */
export function npcHome(npc: NpcDef, layout: TownLayout): BuildingDef | null {
  if (npc.home >= 0) return layout.buildings[npc.home] ?? null;
  const dwellings: number[] = [];
  for (let i = 0; i < layout.buildings.length; i++) {
    if (layout.buildings[i].kind === "dwelling") dwellings.push(i);
  }
  if (dwellings.length === 0) return null;
  const s = seedInt(npc.id);
  let best = dwellings[hash32(s, 1) % dwellings.length];
  let bd = Infinity;
  for (let k = 0; k < 4; k++) {
    const b = layout.buildings[dwellings[hash32(s, 2 + k) % dwellings.length]];
    const d = pmath.hypot(b.u - npc.u, b.v - npc.v);
    if (d < bd) {
      bd = d;
      best = dwellings[hash32(s, 2 + k) % dwellings.length];
    }
  }
  return layout.buildings[best];
}

/**
 * Personal clock offset (seconds): everyone wakes/sleeps a little apart
 * (±3.5% of the day — no lockstep town), and ~5% are NIGHT OWLS living
 * ~0.45 day shifted so the deep-night lanes aren't perfectly empty. The
 * whole pose function evaluates at the shifted time, so schedules stay
 * internally consistent (and DAYTIME keeps the crowd: 95% wake with the
 * sun).
 */
export function npcClockShiftForId(id: string, periodS: number): number {
  const s = seedInt(id);
  const stagger = (h01(hash32(s, 77)) - 0.5) * 0.07;
  const owl = h01(hash32(s, 99)) < 0.015 ? 0.45 : 0;
  return (stagger + owl) * periodS;
}

export function npcClockShift(npc: NpcDef, periodS: number): number {
  return npcClockShiftForId(npc.id, periodS);
}

/** The door-step point of a building (front face, +z in its frame). */
function doorPoint(b: BuildingDef): [number, number] {
  const off = b.d / 2 + 1.1;
  return [b.u - pmath.sin(b.yaw) * off, b.v + pmath.cos(b.yaw) * off];
}

/** Cached hall lookup — the linear scan is 2000+ buildings in a city. */
const HALLS = new WeakMap<TownLayout, BuildingDef | null>();
function layoutHall(layout: TownLayout): BuildingDef | null {
  let hall = HALLS.get(layout);
  if (hall === undefined) {
    hall = layout.buildings.find((b) => b.kind === "hall") ?? null;
    HALLS.set(layout, hall);
  }
  return hall;
}

function anchorPoint(
  npc: NpcDef,
  town: TownDef,
  layout: TownLayout,
  slot: NpcScheduleSlot,
  dayIndex: number,
  slotIdx: number,
): [number, number] {
  const s = seedInt(npc.id);
  switch (slot.anchor) {
    case "home": {
      const home = npcHome(npc, layout);
      return home ? doorPoint(home) : [npc.u, npc.v];
    }
    case "work":
      return [npc.u, npc.v];
    case "plaza": {
      const a = h01(hash32(s, dayIndex, slotIdx, 7)) * Math.PI * 2;
      const rr = layout.plazaR * (0.35 + 0.5 * h01(hash32(s, dayIndex, slotIdx, 8)));
      return [layout.plazaU + pmath.cos(a) * rr, layout.plazaV + pmath.sin(a) * rr];
    }
    // Legacy TOWN layouts have no parcel uses — the 2026-07-31 venue anchors
    // (market/shrine/tavern) resolve to the hall gathering point here; the
    // region path (life.ts) routes them to their real parcels.
    case "market":
    case "shrine":
    case "tavern":
    case "hall": {
      const hall = layoutHall(layout);
      if (hall) {
        const [du, dv] = doorPoint(hall);
        const a = h01(hash32(s, dayIndex, slotIdx, 9)) * Math.PI * 2;
        return [du + pmath.cos(a) * 2.5, dv + pmath.sin(a) * 2.5];
      }
      return [layout.plazaU, layout.plazaV];
    }
    case "stroll": {
      // A fresh spot each stroll slot: somewhere on the fabric.
      const a = h01(hash32(s, dayIndex, slotIdx, 11)) * Math.PI * 2;
      const rr = townRadiusAt(town, a) * (0.25 + 0.55 * h01(hash32(s, dayIndex, slotIdx, 12)));
      return [pmath.cos(a) * rr, pmath.sin(a) * rr];
    }
  }
}

// ---------------------------------------------------------------------------
// The day plan + the analytic pose
// ---------------------------------------------------------------------------

export interface DaySegment {
  t0: number; // dayFrac start (walk begins here)
  activity: NpcActivity;
  u: number;
  v: number;
  /** Region plans (P4 indoor life): the parcel this slot anchors to, when known. */
  parcel?: number | null;
}

/** LRU-ish plan cache — npcPoseAt runs per NPC per frame in the client's
 *  near-field, and a plan build resolves 5 hashed anchors. Values are pure
 *  functions of the key, so caching cannot change any result. */
const PLANS = new Map<string, DaySegment[]>();

/** One NPC's day: jittered slot starts + resolved anchors. Pure. */
export function npcDayPlan(npc: NpcDef, town: TownDef, dayIndex: number): DaySegment[] {
  const key = npc.id + "|" + dayIndex;
  const cached = PLANS.get(key);
  if (cached) return cached;
  const layout = townLayout(town);
  const slots = NPC_SCHEDULES[npc.role];
  const s = seedInt(npc.id);
  const plan = slots.map((slot, i) => {
    // ±0.02 of the day (order-safe: the tightest template gap is 0.06) —
    // with the ±0.035 personal clock stagger, a slot turnover spreads over
    // ~11% of the day instead of everyone marching off at once.
    const jitter = (h01(hash32(s, dayIndex, i)) * 2 - 1) * 0.02;
    // Anchors never land INSIDE a building — hashed offsets (a workshop
    // spot, a plaza edge) could park people in walls (user-reported).
    const [u, v] = navSnapFree(town, ...anchorPoint(npc, town, layout, slot, dayIndex, i));
    return { t0: slot.t + jitter, activity: slot.activity, u, v };
  });
  PLANS.set(key, plan);
  if (PLANS.size > 16384) {
    // Drop the oldest half — plans are tiny and rebuilt on demand.
    let n = 8192;
    for (const k of PLANS.keys()) {
      PLANS.delete(k);
      if (--n <= 0) break;
    }
  }
  return plan;
}

/**
 * WHERE IS EVERYONE — the analytic pose at wall-clock tSec. The current
 * segment's anchor, a straight walk from the previous anchor at NPC_WALK,
 * and 90-second micro-drift while idling. No state, no integration.
 */
/** Scratch for pathAt (single-threaded evaluators; values copied out). */
const PATH_AT = { u: 0, v: 0, heading: 0 };

// townDayFrac at the UNSHIFTED query time, memoized — a metropolis
// evaluates hundreds of poses against the same (town, tSec) per frame,
// and the azimuth math dominates the whole pose. Pure function of the
// key, so caching cannot change any result.
let dfKey = "";
let dfVal = 0;
function townDayFracMemo(def: SystemDef, planetIdx: number, town: TownDef, tSec: number): number {
  const key = planetIdx + "|" + town.seed + "|" + tSec;
  if (key === dfKey) return dfVal;
  dfKey = key;
  dfVal = townDayFrac(def, planetIdx, town, tSec);
  return dfVal;
}

export function npcPoseAt(
  npc: NpcDef,
  town: TownDef,
  def: SystemDef,
  planetIdx: number,
  tSec: number,
): NpcPose {
  const period = def.planets[planetIdx].spin.periodS;
  // Personal clock: the pose function of a staggered/nocturnal persona is
  // the base function at their shifted time. dayFrac is linear in time
  // (1/period, the slow orbital sun-drift folds into the shift harmlessly
  // — every evaluator applies the identical formula), so the shift is
  // applied ARITHMETICALLY to one memoized base evaluation per (town, t)
  // instead of re-running the azimuth math per persona.
  const shift = npcClockShift(npc, period);
  tSec -= shift;
  const fRaw = townDayFracMemo(def, planetIdx, town, tSec + shift) - shift / period;
  const dayFrac = fRaw - Math.floor(fRaw);
  const dayIndex = Math.floor(tSec / period - dayFrac + 0.5);
  const plan = npcDayPlan(npc, town, dayIndex);
  // Find the active segment (wrapping: before the first slot = yesterday's
  // last slot, i.e. asleep at home).
  let seg = plan[plan.length - 1];
  let prev = plan[plan.length - 2] ?? seg;
  let segFromYesterday = true;
  let segIdx = plan.length - 1;
  let nextStartFrac = plan[0].t0 + 1; // tomorrow's first slot (approx)
  for (let i = 0; i < plan.length; i++) {
    if (dayFrac >= plan[i].t0) {
      seg = plan[i];
      prev = i > 0 ? plan[i - 1] : npcDayPlan(npc, town, dayIndex - 1)[plan.length - 1];
      segFromYesterday = false;
      segIdx = i;
      nextStartFrac = i + 1 < plan.length ? plan[i + 1].t0 : plan[0].t0 + 1;
    }
  }
  if (segFromYesterday) {
    const yPlan = npcDayPlan(npc, town, dayIndex - 1);
    seg = yPlan[yPlan.length - 1];
    prev = yPlan[yPlan.length - 2] ?? seg;
    nextStartFrac = plan[0].t0; // today's first slot ends yesterday's night
  }
  // Seconds until this segment hands off to the next (drives the drift
  // chain's RETURN-TO-ANCHOR so the next walk starts exactly where the
  // idling actually ended — the old anchor-jump popped ~6 m per slot).
  const toEndSec = (((nextStartFrac - dayFrac) % 1) + 1) % 1 * period;
  // Walk phase: distance / personal walk speed after segment start. The
  // route comes from the town NAV GRID (npcNav) — people round the
  // buildings instead of phasing through them, and every evaluator
  // derives the identical polyline.
  const walk = npcWalkSpeed(npc);
  const sinceStart = ((dayFrac - seg.t0 + 1) % 1) * period; // seconds into segment
  const s = seedInt(npc.id);
  let walkTime = 0;
  if (pmath.hypot(seg.u - prev.u, seg.v - prev.v) > 1) {
    const path = walkPath(town, prev.u, prev.v, seg.u, seg.v);
    walkTime = path.len / walk;
    if (sinceStart < walkTime) {
      const d = sinceStart * walk;
      pathAt(path, d, PATH_AT);
      // Personal lane: a fixed lateral offset (eased at the ends so the
      // walk still starts/stops exactly at the anchors) — a popular route
      // shouldn't read as single file. Amplitude stays UNDER the nav
      // grid's 1.0 m building margin so lanes never clip a wall.
      const lane =
        (h01(hash32(s, 61)) - 0.5) * 1.8 * Math.min(1, d / 6, (path.len - d) / 6);
      return {
        u: PATH_AT.u - pmath.sin(PATH_AT.heading) * lane,
        v: PATH_AT.v + pmath.cos(PATH_AT.heading) * lane,
        activity: seg.activity,
        moving: true,
        heading: PATH_AT.heading,
        speed: walk,
        dayFrac,
        dayIndex,
      };
    }
  }
  // WORK (Tier 3): jobs with a motion pattern own the whole work slot —
  // the farmer paces + tends the crop line, the warden walks the gate
  // road. Both ease back to the anchor as the slot ends (the next walk
  // starts there).
  if (seg.activity === "work" && (npc.job === "field" || npc.job === "patrol")) {
    const jd = npc.jobData;
    const len = pmath.hypot(jd[2] - jd[0], jd[3] - jd[1]) || 1;
    const paceV = npc.job === "field" ? Math.max(0.55, walk * 0.4) : walk * 0.8;
    const dwell = npc.job === "field" ? 13 : 6;
    const legT = len / paceV;
    const cycle = 2 * (legT + dwell);
    const since = Math.max(0, sinceStart - walkTime);
    const ph = since % cycle;
    let k: number;
    let mv = false;
    let back = false;
    if (ph < legT) {
      k = ph / legT;
      mv = true;
    } else if (ph < legT + dwell) {
      k = 1;
    } else if (ph < 2 * legT + dwell) {
      k = 1 - (ph - legT - dwell) / legT;
      mv = true;
      back = true;
    } else {
      k = 0;
    }
    // Slot ending: the reachable span shrinks at pace speed, walking them
    // home to the line start (= the anchor) for a seamless handoff.
    const kEnd = Math.max(0, Math.min(1, (toEndSec - dwell) / legT));
    if (k > kEnd) {
      k = kEnd;
      mv = true;
      back = true;
    }
    const dwelling = !mv;
    return {
      u: jd[0] + (jd[2] - jd[0]) * k,
      v: jd[1] + (jd[3] - jd[1]) * k,
      activity: seg.activity,
      moving: mv,
      heading: mv
        ? pmath.atan2((back ? jd[1] - jd[3] : jd[3] - jd[1]), (back ? jd[0] - jd[2] : jd[2] - jd[0]))
        : pmath.atan2(jd[3] - jd[1], jd[2] - jd[0]) + Math.PI / 2, // face the row
      speed: mv ? paceV : 0,
      dayFrac,
      dayIndex,
      crouch: dwelling && npc.job === "field",
    };
  }

  // Idle drift: a fresh micro-spot every 90 s inside the activity radius
  // (snapped out of walls — a 6 m roam can poke into the neighbor's house).
  // The move between spots is a GLIDED micro-stroll, continuous at every
  // boundary — the old instant teleport read as "not anchored to the
  // ground" (user-reported). Chain: walk-arrival → spot(b) → spot(b+1)…
  const radius = seg.activity === "sleep" ? 0 : seg.activity === "work" ? Math.min(6, npc.roam) : 3.5;
  const bucket = Math.floor(tSec / 90);
  const spot = (bk: number): [number, number] => {
    const da = h01(hash32(s, bk, 31)) * Math.PI * 2;
    const dr = radius * Math.sqrt(h01(hash32(s, bk, 32)));
    return navSnapFree(town, seg.u + pmath.cos(da) * dr, seg.v + pmath.sin(da) * dr);
  };
  const arrivalT = tSec - sinceStart + walkTime; // when the walk ended (shifted clock)
  const ab = Math.floor(arrivalT / 90);
  if (radius <= 0) {
    return {
      u: seg.u,
      v: seg.v,
      activity: seg.activity,
      moving: false,
      heading: h01(hash32(s, dayIndex, segIdx * 7 + 33)) * Math.PI * 2,
      speed: 0,
      dayFrac,
      dayIndex,
    };
  }
  // Walk the hop chain from arrival — wall-clipped hops are SKIPPED (stand
  // pat), so the chain tracks where each hop actually ENDED (continuity).
  let fx = seg.u;
  let fz = seg.v;
  for (let bk = ab; bk < bucket; bk++) {
    const sp = spot(bk);
    if (navLosFree(town, fx, fz, sp[0], sp[1])) {
      fx = sp[0];
      fz = sp[1];
    }
  }
  const from: [number, number] = [fx, fz];
  // Near the segment's end, the hop RETURNS to the anchor — the next walk
  // begins there, so the handoff is seamless. (120 s always spans at least
  // one 90 s hop boundary.)
  let to = toEndSec < 120 ? ([seg.u, seg.v] as [number, number]) : spot(bucket);
  if (!navLosFree(town, from[0], from[1], to[0], to[1])) to = from;
  const glideStart = bucket <= ab ? arrivalT : bucket * 90;
  const dd = pmath.hypot(to[0] - from[0], to[1] - from[1]);
  // Complete before the bucket rolls (continuity: the next hop starts from
  // THIS hop's endpoint), never slower than the personal walk pace.
  const glideT = Math.max(0.2, Math.min(Math.max(0.8, dd / walk), (bucket + 1) * 90 - glideStart));
  const k = Math.min(1, Math.max(0, tSec - glideStart) / glideT);
  const gliding = k < 1 && dd > 0.4;
  // Idle facing is STABLE for the whole segment (a fresh random heading
  // per 90 s bucket had standing people whirling — probe-caught); the
  // stand-at jobs face their WORK instead (the stall, the bench, the
  // well), and the artisan drops to the bench now and then.
  let idleHeading = h01(hash32(s, dayIndex, segIdx * 7 + 33)) * Math.PI * 2;
  let idleCrouch = false;
  if (
    seg.activity === "work" &&
    (npc.job === "stall" || npc.job === "shopfront" || npc.job === "hall" || npc.job === "well")
  ) {
    idleHeading = pmath.atan2(npc.jobData[1] - seg.v, npc.jobData[0] - seg.u);
    if (npc.job === "shopfront") idleCrouch = h01(hash32(s, bucket, 41)) < 0.35;
  }
  return {
    u: from[0] + (to[0] - from[0]) * k,
    v: from[1] + (to[1] - from[1]) * k,
    activity: seg.activity,
    moving: gliding,
    heading: gliding ? pmath.atan2(to[1] - from[1], to[0] - from[0]) : idleHeading,
    speed: gliding ? Math.min(walk, dd / glideT) : 0,
    dayFrac,
    dayIndex,
    crouch: !gliding && idleCrouch,
  };
}

/**
 * PASSING GREETINGS — deterministic pair emotes with zero negotiation.
 * When two awake NPCs' analytic poses come within 6 m during a 12-second
 * window, both turn their heads / wave (client-rendered; every observer
 * computes the same moment). Returns the partner's roster index or -1.
 */
export function npcMeeting(
  poses: NpcPose[],
  roster: NpcDef[],
  i: number,
  tSec: number,
): number {
  const window = Math.floor(tSec / 12);
  const a = poses[i];
  if (a.activity === "sleep") return -1;
  for (let j = 0; j < roster.length; j++) {
    if (j === i) continue;
    const b = poses[j];
    if (!b || b.activity === "sleep") continue;
    const d = pmath.hypot(a.u - b.u, a.v - b.v);
    if (d > 6) continue;
    const lo = Math.min(i, j);
    const hi = Math.max(i, j);
    if (h01(hash32(seedInt(roster[lo].id), seedInt(roster[hi].id), window)) < 0.5) return j;
  }
  return -1;
}
